// Modal de geração de proposta — selecionar modelo + composição, ver preview e
// gerar a versão final. Exportado pra uso direto em NegocioDetalhe.
// Extraído de ComercialPipeline.tsx em 2026-05-23 (frontend wave 3B).
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Select, Modal, Form, Typography, Space,
  Row, Col, App as AntApp, Flex,
} from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import PainelAnexosNegocio from '../../components/PainelAnexosNegocio';

const { Text } = Typography;

// Modal de geração de proposta — mantido com props compatíveis (exportado pra uso em NegocioDetalhe)
export function ModalGerarProposta({ propostaId, tituloNegocio, responsavel, vendedores, onClose, onGerada }: {
  propostaId: number;
  tituloNegocio: string;
  responsavel: string;
  vendedores: { email: string; nome: string; telefone?: string }[];
  onClose: () => void;
  onGerada: () => void;
}) {
  return (
    <AntApp>
      <ModalGerarPropostaInner propostaId={propostaId} tituloNegocio={tituloNegocio}
        responsavel={responsavel} vendedores={vendedores} onClose={onClose} onGerada={onGerada} />
    </AntApp>
  );
}

function ModalGerarPropostaInner({ propostaId, tituloNegocio, responsavel, vendedores, onClose, onGerada }: {
  propostaId: number;
  tituloNegocio: string;
  responsavel: string;
  vendedores: { email: string; nome: string; telefone?: string }[];
  onClose: () => void;
  onGerada: () => void;
}) {
  const { message } = AntApp.useApp();
  const [modelos, setModelos] = useState<any[]>([]);
  const [composicoes, setComposicoes] = useState<any[]>([]);
  const [modeloId, setModeloId] = useState('');
  const [composicaoId, setComposicaoId] = useState('');
  const [titulo, setTitulo] = useState(tituloNegocio);
  const [previewHtml, setPreviewHtml] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [mRes, cRes] = await Promise.all([
          axios.get(`${API_URL}/comercial/modelos-proposta`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/comercial/composicoes?proposta_id=${propostaId}`, { headers: { Authorization: token } }),
        ]);
        const m = mRes.data.body ? JSON.parse(mRes.data.body) : mRes.data;
        const c = cRes.data.body ? JSON.parse(cRes.data.body) : cRes.data;
        setModelos(m); setComposicoes(c);
        if (m.length === 1) setModeloId(m[0].id);
        if (c.length === 1) setComposicaoId(c[0].id);
      } finally { setCarregando(false); }
    })();
  }, [propostaId]);

  useEffect(() => {
    if (!modeloId) { setPreviewHtml(''); return; }
    (async () => {
      setPreviewLoading(true);
      try {
        const token = await getToken();
        const vendedor = vendedores.find(v => v.nome === responsavel);
        const res = await axios.post(`${API_URL}/comercial/propostas-geradas/preview`, {
          proposta_id: propostaId, composicao_id: composicaoId || null, modelo_id: modeloId,
          titulo: titulo.trim() || tituloNegocio,
          vendedor: vendedor ? { nome: vendedor.nome, email: vendedor.email, telefone: vendedor.telefone || '' } : undefined,
        }, { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        setPreviewHtml(data.html || '');
      } catch (e: any) {
        setPreviewHtml(`<div style="color:#B91626">Erro: ${e.message}</div>`);
      } finally { setPreviewLoading(false); }
    })();
  }, [modeloId, composicaoId, propostaId, responsavel, vendedores, titulo, tituloNegocio]);

  async function gerar() {
    if (!modeloId) { message.warning('Escolha um modelo'); return; }
    setGerando(true);
    try {
      const token = await getToken();
      const vendedor = vendedores.find(v => v.nome === responsavel);
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        proposta_id: propostaId, composicao_id: composicaoId || null, modelo_id: modeloId,
        titulo: titulo.trim() || tituloNegocio,
        vendedor: vendedor ? { nome: vendedor.nome, email: vendedor.email, telefone: vendedor.telefone || '' } : undefined,
      }, { headers: { Authorization: token } });
      message.success('Proposta gerada.');
      onGerada();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setGerando(false); }
  }

  return (
    <Modal open onCancel={onClose} footer={null} width={1340} destroyOnClose
      title={`📄 Gerar proposta — ${tituloNegocio}`}
      styles={{ body: { padding: 0 } }}>
      <Flex>
        <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '80vh' }}>
          {carregando ? <Card loading /> : (
            <>
              <Form layout="vertical" size="small">
                <Form.Item label={
                  <Space>
                    <Text strong style={{ fontSize: 11 }}>Título da proposta</Text>
                    <Text type="secondary" style={{ fontSize: 10 }}>(aparece na capa, pode ser mais descritivo)</Text>
                  </Space>
                }>
                  <Input value={titulo} onChange={e => setTitulo(e.target.value)}
                    placeholder={`Ex.: Proposta Técnica e Comercial — ${tituloNegocio}`} />
                </Form.Item>
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item label="Modelo" required
                      extra={modelos.length === 0 ? <Text style={{ color: '#B91626', fontSize: 10 }}>Nenhum modelo cadastrado. Vá em Comercial → Propostas → Modelos.</Text> : ''}>
                      <Select value={modeloId} onChange={setModeloId} placeholder="Selecione um modelo"
                        options={modelos.map(m => ({ label: `${m.nome}${m.tipo ? ` (${m.tipo})` : ''}`, value: m.id }))} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Composição vinculada">
                      <Select value={composicaoId || undefined} onChange={(v) => setComposicaoId(v || '')}
                        placeholder="Sem composição (variáveis vazias)" allowClear
                        options={composicoes.map(c => ({ label: c.nome, value: c.id }))} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              <Text type="secondary" style={{ fontSize: 11, marginBottom: 6 }}>Preview:</Text>
              <Card size="small" bodyStyle={{ padding: 24, minHeight: 300, maxHeight: '50vh', overflow: 'auto' }}>
                {previewLoading
                  ? <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>Gerando preview...</Text>
                  : previewHtml
                    ? <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    : <Text type="secondary" italic style={{ textAlign: 'center', display: 'block' }}>Selecione um modelo para ver o preview.</Text>}
              </Card>

              <Flex justify="flex-end" gap={10} style={{ marginTop: 14 }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="primary" disabled={!modeloId} loading={gerando} onClick={gerar}>
                  Gerar e abrir
                </Button>
              </Flex>
            </>
          )}
        </div>
        <PainelAnexosNegocio negocioId={propostaId} />
      </Flex>
    </Modal>
  );
}
